import React, { Fragment, useEffect, useRef, useState } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { showToast } from "../../../components/ToastifyNotification";
import { GetUsers } from "../../../api/utility/userAPI";
import { SendMessage } from "../../../api/messaging/messageAPI";
import useKeyboardSubmit from "../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../hooks/useEnterNavigation";

/* ---------------------------------
   VALIDATION SCHEMA
----------------------------------*/
const schema = yup.object().shape({
  toUserIds: yup
    .array()
    .min(1, "At least one recipient is required")
    .required(),

  ccUserIds: yup.array().optional(),

  subject: yup.string().required("Subject is required"),
  message: yup.string().required("Message is required")
});

const Send = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(state => state?.authReducer?.user || {});
  const [users, setUsers] = useState([]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      toUserIds: [],
      ccUserIds: []
    }
  });

  /* ---------------------------------
     LOAD USERS
  ----------------------------------*/
  useEffect(() => {
    const fetchUsers = async () => {
      const res = await GetUsers();
      if (res?.success) {
        setUsers(
          res.data
            .filter(u => u._id !== user._id)
            .map(u => ({
              value: u._id,
              label: u.userName
            }))
        );
      }
    };
    fetchUsers();
  }, []);

  /* ---------------------------------
     SUBMIT
  ----------------------------------*/
  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      const payload = {
        subject: data.subject,
        message: data.message,
        toUserIds: data.toUserIds.map(u => u.value),
        ccUserIds: data.ccUserIds.map(u => u.value)
      };

      const response = await SendMessage(payload);

      if (response.success) {
        showToast("success", response.message);
        navigate("/messaging/outbox");
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
        useKeyboardSubmit(handleSubmit, onSubmit);
        useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Messages" title="Compose Mail" />

      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-8">
            <div className="card shadow-sm">
              <div className="card-header">
                <h5 className="mb-0">New Message</h5>
              </div>

              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body">

                  {/* TO */}
                  <div className="form-group mb-3">
                    <label className="form-label">To</label>
                    <Controller
                      name="toUserIds"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={users}
                          isMulti
                          placeholder="Select recipients..."
                          classNamePrefix="react-select"
                        />
                      )}
                    />
                    {errors.toUserIds && (
                      <small className="text-danger">
                        {errors.toUserIds.message}
                      </small>
                    )}
                  </div>

                  {/* CC */}
                  <div className="form-group mb-3">
                    <label className="form-label">Cc</label>
                    <Controller
                      name="ccUserIds"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={users}
                          isMulti
                          placeholder="Select CC recipients..."
                          classNamePrefix="react-select"
                        />
                      )}
                    />
                  </div>

                  {/* SUBJECT */}
                  <div className="form-group mb-3">
                    <label className="form-label">Subject</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Subject"
                      {...register("subject")}
                    />
                    {errors.subject && (
                      <small className="text-danger">
                        {errors.subject.message}
                      </small>
                    )}
                  </div>

                  {/* MESSAGE */}
                  <div className="form-group">
                    <label className="form-label">Message</label>
                    <textarea
                      className="form-control"
                      rows="8"
                      placeholder="Write your message..."
                      {...register("message")}
                    />
                    {errors.message && (
                      <small className="text-danger">
                        {errors.message.message}
                      </small>
                    )}
                  </div>
                </div>

                {/* FOOTER */}
                <div className="card-footer d-flex justify-content-between">
                  <button type="submit" className="btn btn-primary px-4">
                    Send
                  </button>

                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => navigate(-1)}
                  >
                    Discard
                  </button>
                </div>
              </form>

            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Send;
